import { Helmet } from "react-helmet-async";

export default function ShellPilotSupport() {
  return (
    <main className="min-h-screen bg-[#FBFAF6] text-[#26313B]">
      <Helmet>
        <title>Support - ShellPilot | Cosmovex</title>
        <meta
          name="description"
          content="Support page for ShellPilot, the SSH terminal and SFTP client by Cosmovex."
        />
        <link
          rel="canonical"
          href="https://cosmovexapps.com/apps/shellpilot/support"
        />
      </Helmet>

      <section className="mx-auto max-w-3xl px-6 py-12">
        <header className="mb-8 text-center">
          <h1 className="text-4xl font-extrabold tracking-tight text-[#A8720E]">
            Support - ShellPilot
          </h1>
          <p className="mt-2 text-sm text-[#5B6B7C]">
            Help, common answers, and contact details for ShellPilot users.
          </p>
          <p className="mt-1 text-xs text-[#8A93A2]">
            Published by <span className="font-semibold">Cosmovex</span>
          </p>
        </header>

        <div className="space-y-8 leading-relaxed text-[#3D4B59]">
          <p>
            <strong>ShellPilot</strong> is a mobile SSH terminal and SFTP client
            for connecting to your servers, running commands, managing files, and
            automating tasks from your pocket.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I connect to my server?
              </h3>
              <p className="mt-1">
                Tap the + button on the home screen, enter your server&apos;s
                hostname, port (default 22), and username, then tap Connect. You
                can authenticate with a password or an imported SSH key.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I import an SSH key?
              </h3>
              <p className="mt-1">
                Go to Settings &gt; SSH Keys &gt; Import Key. You can import
                keys from your device&apos;s file system. Keys are stored
                securely in your device&apos;s keychain and are never uploaded to
                our servers.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                What does ShellPilot Pro include?
              </h3>
              <p className="mt-1">
                Pro is a one-time purchase that unlocks unlimited connections,
                SSH keys, and snippets, full SFTP write access (upload, rename,
                create, delete), all terminal themes and fonts, and an ad-free
                experience.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I restore my Pro purchase on a new device?
              </h3>
              <p className="mt-1">
                Sign in with the same Google or Apple account you used when you
                purchased Pro, then go to Settings &gt; Upgrade to Pro &gt;
                Restore Purchases. Your entitlement will sync automatically.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Can I use ShellPilot on both Android and iOS?
              </h3>
              <p className="mt-1">
                Yes. ShellPilot is available on both platforms. However, in-app
                purchases are tied to the respective app store (Google Play or
                App Store) and cannot be transferred between platforms.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                Is my data secure?
              </h3>
              <p className="mt-1">
                SSH keys are stored exclusively in your device&apos;s secure
                storage (Keychain on iOS, Keystore on Android). Connection
                profiles are stored locally. Server-side data is protected by
                security rules that restrict access to your own data only.
              </p>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-[#26313B]">
                How do I delete my account?
              </h3>
              <p className="mt-1">
                Email us at{" "}
                <a
                  href="mailto:trionapps@gmail.com?subject=ShellPilot%20Account%20Deletion"
                  className="text-[#0F6BBF] underline"
                >
                  trionapps@gmail.com
                </a>{" "}
                from the email associated with your account. See our{" "}
                <a
                  href="/apps/shellpilot/account-deletion"
                  className="text-[#0F6BBF] underline"
                >
                  Account Deletion page
                </a>{" "}
                for details.
              </p>
            </div>
          </div>

          <h2 className="text-2xl font-bold text-[#A8720E]">Contact Support</h2>
          <p>
            For help, bug reports, or privacy-related questions, email{" "}
            <a
              href="mailto:trionapps@gmail.com"
              className="text-[#0F6BBF] underline"
            >
              trionapps@gmail.com
            </a>
            . We usually respond within 24-48 hours.
          </p>

          <h2 className="text-2xl font-bold text-[#A8720E]">Report a Bug</h2>
          <p>
            If something is not working as expected, include these details in
            your email:
          </p>
          <ul className="list-disc space-y-2 pl-6">
            <li>Your device model and OS version</li>
            <li>Your app version</li>
            <li>What you were trying to do</li>
            <li>The error or unexpected result</li>
            <li>Steps to reproduce the issue, if known</li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">Important Links</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              Privacy Policy:{" "}
              <a
                href="/apps/shellpilot/privacy-policy"
                className="text-[#0F6BBF] underline"
              >
                https://cosmovexapps.com/apps/shellpilot/privacy-policy
              </a>
            </li>
            <li>
              Account Deletion:{" "}
              <a
                href="/apps/shellpilot/account-deletion"
                className="text-[#0F6BBF] underline"
              >
                https://cosmovexapps.com/apps/shellpilot/account-deletion
              </a>
            </li>
          </ul>

          <h2 className="text-2xl font-bold text-[#A8720E]">App Information</h2>
          <ul className="list-disc space-y-2 pl-6">
            <li>
              <strong>App Name:</strong> ShellPilot
            </li>
            <li>
              <strong>Developer:</strong> Cosmovex
            </li>
            <li>
              <strong>Contact Email:</strong>{" "}
              <a
                href="mailto:trionapps@gmail.com"
                className="text-[#0F6BBF] underline"
              >
                trionapps@gmail.com
              </a>
            </li>
          </ul>
        </div>

        <footer className="mt-12 border-t border-[#E1DCCE] pt-4 text-center text-sm text-[#8A93A2]">
          © {new Date().getFullYear()} Cosmovex - All rights reserved.
        </footer>
      </section>
    </main>
  );
}
